import { forwardRef, type ReactNode, type RefAttributes } from "react";
import classNames from "classnames";
import { styled, type Theme } from "~/common/mui";
import {
  C_PRIMARY_DISABLED,
  C_PRIMARY_HOVER,
  C_SECONDARY_DISABLED,
  C_SECONDARY_HOVER,
} from "~/common/components/icon/Colors";
import { IconSize } from "~/common/mui/icons/IconSize";
import { resolveUrl } from "../../../utils/url/urlResolver";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export type LinkProps = JSX.IntrinsicElements["a"] &
  RefAttributes<HTMLAnchorElement> & {
    variant?: LinkType;
    disabled?: boolean;
    iconBefore?: ReactNode;
    iconAfter?: ReactNode;
    skipResolve?: boolean;
    children?: ReactNode;
  };

export enum LinkType {
  PRIMARY = "scha__link--primary",
  SECONDARY = "scha__link--secondary",
  NAVIGATION = "scha__link--navigation",
  INHERIT = "scha__link--inherit",
}

type LinkRootStyleProps = {
  $variant: LinkType;
  $disabled?: boolean;
};

type LinkIconPosition = "before" | "after";
type LinkStyle = Record<string, unknown>;

const LINK_ICON_COLOR_VAR = "--scha-link-icon-color";

const linkRootStyles = ({ theme, $variant, $disabled }: LinkRootStyleProps & { theme: Theme }): LinkStyle => {
  const styles: LinkStyle = {
    cursor: $disabled ? "default" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    transition: "color 0.2s ease",
  };

  if ($disabled) {
    styles.pointerEvents = "none";
  }

  if ($variant === LinkType.PRIMARY) {
    styles.color = $disabled ? C_PRIMARY_DISABLED : theme.palette.primary.main;
    if (!$disabled) {
      styles["&:hover"] = { color: C_PRIMARY_HOVER };
    }
  }

  if ($variant === LinkType.SECONDARY) {
    styles.color = $disabled ? C_SECONDARY_DISABLED : theme.palette.text.primary;
    styles[LINK_ICON_COLOR_VAR] = $disabled ? "inherit" : theme.palette.text.secondary;
    if (!$disabled) {
      styles["&:hover"] = {
        color: C_SECONDARY_HOVER,
        [LINK_ICON_COLOR_VAR]: "inherit",
      };
    }
  }

  if ($variant === LinkType.NAVIGATION) {
    styles.color = $disabled ? C_SECONDARY_DISABLED : theme.palette.text.primary;
    if ($disabled) {
      styles[LINK_ICON_COLOR_VAR] = "inherit";
    } else {
      styles["&:hover"] = { color: theme.palette.primary.main };
    }
  }

  if ($variant === LinkType.INHERIT) {
    styles.color = "inherit";
    if ($disabled) {
      styles.opacity = 0.38;
    } else {
      styles["&:hover"] = {
        color: "inherit",
        textDecoration: "underline",
      };
    }
  }

  return styles;
};

const LinkRoot = styled("a", {
  shouldForwardProp: (prop) => prop !== "$variant" && prop !== "$disabled",
})<LinkRootStyleProps>(linkRootStyles);

const LinkButtonRoot = styled("button", {
  shouldForwardProp: (prop) => prop !== "$variant" && prop !== "$disabled",
})<LinkRootStyleProps>((props) => ({
  appearance: "none",
  background: "none",
  border: 0,
  margin: 0,
  padding: 0,
  font: "inherit",
  textAlign: "inherit",
  ...linkRootStyles(props),
}));

const LinkIcon = styled("span", {
  shouldForwardProp: (prop) => prop !== "$position",
})<{ $position: LinkIconPosition }>(({ theme, $position }) => ({
  display: "inherit",
  color: `var(${LINK_ICON_COLOR_VAR}, inherit)`,
  ...($position === "before" && {
    marginRight: theme.spacing(1),
  }),
  ...($position === "after" && {
    marginLeft: theme.spacing(1),
  }),
}));

const ArrowLinkIcon = styled(KeyboardArrowRightIcon)(({ theme }) => ({
  transform: "translateX(-33.3333%)",
  marginRight: theme.spacing(-0.625),
}));

const BackLinkIcon = styled(KeyboardArrowLeftIcon)(({ theme }) => ({
  transform: "translateX(-33.3333%)",
  marginRight: theme.spacing(-0.625),
}));

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    variant = LinkType.PRIMARY,
    className,
    children,
    disabled,
    iconBefore: iconBeforeProp,
    iconAfter: iconAfterProp,
    href,
    skipResolve,
    ...anchorProps
  } = props;
  const cls = classNames("scha__link", variant, className, disabled && "disabled");

  const iconBefore = iconBeforeProp && (
    <LinkIcon $position="before" className={"scha__link__icon scha__link__icon--before"}>
      {iconBeforeProp}
    </LinkIcon>
  );

  const iconAfter = iconAfterProp && (
    <LinkIcon $position="after" className={"scha__link__icon scha__link__icon--after"}>
      {iconAfterProp}
    </LinkIcon>
  );

  let url = undefined;
  if (href) {
    if (skipResolve) {
      url = href;
    } else {
      url = resolveUrl(href);
    }
  }

  if ("dangerouslySetInnerHTML" in anchorProps) {
    return <LinkRoot $variant={variant} $disabled={disabled} className={cls} href={url} {...anchorProps} ref={ref} />;
  } else {
    return (
      <LinkRoot $variant={variant} $disabled={disabled} className={cls} href={url} {...anchorProps} ref={ref}>
        {iconBefore}
        {children}
        {iconAfter}
      </LinkRoot>
    );
  }
});

export interface LinkButtonProps extends Omit<JSX.IntrinsicElements["button"], "dangerouslySetInnerHTML" | "ref"> {
  variant?: LinkType;
  disabled?: boolean;
  iconBefore?: ReactNode;
  children?: ReactNode;
}

/**
 * Button that uses Link component styles.
 */
export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>((props, ref) => {
  const { variant = LinkType.PRIMARY, className, children, disabled, iconBefore: iconBeforeProp, ...btnProps } = props;
  const cls = classNames("btn-style-reset scha__link", variant, className, disabled && "disabled");

  const iconBefore = iconBeforeProp && (
    <LinkIcon $position="before" className={"scha__link__icon scha__link__icon--before"}>
      {iconBeforeProp}
    </LinkIcon>
  );

  return (
    <LinkButtonRoot $variant={variant} $disabled={disabled} className={cls} {...btnProps} ref={ref}>
      {iconBefore}
      {children}
    </LinkButtonRoot>
  );
});

export type ArrowLinkProps = Omit<LinkProps, "iconBefore"> & { iconSize?: IconSize };

const iconSizeToFontSize = (iconSize?: IconSize): number => {
  switch (iconSize) {
    case IconSize.FONT_10:
      return 10;
    case IconSize.FONT_20:
      return 20;
    case IconSize.FONT_24:
      return 24;
    case IconSize.FONT_30:
      return 30;
    case IconSize.FONT_50:
      return 50;
    case IconSize.FONT_64:
      return 64;
    case IconSize.FONT_16:
    default:
      return 16;
  }
};

export const ArrowLink = forwardRef<HTMLAnchorElement, ArrowLinkProps>((props, ref) => {
  const { iconSize, ...rest } = props;
  return (
    <Link
      iconBefore={
        <ArrowLinkIcon className={"scha__link--arrow-link"} sx={{ fontSize: iconSizeToFontSize(iconSize) }} />
      }
      ref={ref}
      {...rest}
    />
  );
});

export const BackLink = forwardRef<HTMLAnchorElement, ArrowLinkProps>((props, ref) => {
  const { iconSize, ...rest } = props;
  return (
    <Link
      iconBefore={<BackLinkIcon className={"scha__link--arrow-link"} sx={{ fontSize: iconSizeToFontSize(iconSize) }} />}
      ref={ref}
      {...rest}
    />
  );
});
